<template>
    <UModal :model-value="open" @update:model-value="$emit('update:open', $event)" :ui="{ width: 'w-full sm:max-w-lg' }">
        <UCard :ui="{ rounded: 'rounded-lg' }">
            <template #header>
                <div class="flex items-center justify-between">
                    <h3 class="text-base font-medium leading-6 text-gray-800">Create Subscription</h3>
                    <UButton color="gray" variant="ghost" icon="i-ph-x-bold" class="-my-1" @click="dismiss()" />
                </div>
            </template>
            <div class="space-y-6">
                <UTabs v-model="selectedTab" :items="[{ label: 'Coupon' }, { label: 'Credential' }]" />

                <UFormGroup name="identifer" :label="sourceType === SubscriptionSourceType.coupon ? 'Coupon Code' : 'Credential ID'" required>
                    <UInput v-model="sourceIdentifier" size="lg" />
                </UFormGroup>
            </div>
            <template #footer>
                <div class="flex items-center justify-end space-x-2">
                    <UButton @click="dismiss()" size="lg" color="white"> Cancel </UButton>
                    <UButton @click="confirm()" :loading="confirming" type="submit" size="lg" color="black"> Create </UButton>
                </div>
            </template>
        </UCard>
    </UModal>
</template>

<script setup lang="ts">
import type { Customer } from '~/types/customer';
import { SubscriptionSourceType } from '~/types/subscription';
import { UserRole } from '~/types/user';

const emit = defineEmits<{
    'update:open': [boolean]
    'reload': [void]
}>()

const props = defineProps<{
    open: boolean
    customer?: Customer
}>()

const toast = useToast()
const auth = useAuthStore()

const confirming = ref<boolean>(false)
const selectedTab = ref(0)
const sourceIdentifier = ref<string>('')

const sourceType = computed(() => selectedTab.value === 0 ? SubscriptionSourceType.coupon : SubscriptionSourceType.credential)

watch(() => props.open, () => reset())

async function confirm() {
    if (!auth.hasRole(UserRole.admin)) {
        toast.add({
            title: 'You do not have permission to extend subscriptions',
            color: 'red',
            icon: 'i-ph-warning-circle-duotone'
        })

        return dismiss()
    }

    if (!props.customer?.id) {
        toast.add({
            title: 'Invalid customer',
            color: 'red',
            icon: 'i-ph-warning-circle-duotone'
        })

        return dismiss()
    }

    confirming.value = true

    try {
        await $fetch(`/api/subscriptions`, {
            method: 'POST',
            body: {
                customer_id: props.customer.id,
                type: sourceType.value,
                identifier: sourceIdentifier.value,
            }
        })

        toast.add({
            title: 'Subscription created successfully!',
            color: 'green',
            icon: 'i-ph-check-circle-duotone'
        })

        emit('reload')
    } catch (error: any) {
        toast.add({
            title: 'Could not create subscription!',
            description: error?.data?.message || 'Please try again later.',
            color: 'red',
            icon: 'i-ph-x-circle-duotone'
        })
    } finally {
        confirming.value = false
        dismiss()
    }
}

function reset() {
    selectedTab.value = 0
    sourceIdentifier.value = ''
}

function dismiss() {
    emit('update:open', false)
}
</script>
